import { useEffect, useMemo, useState } from 'react';
import MainTitle from '../../../../components/template/MainTitle'
import ButtonWrap from '../../../../components/template/ButtonWrap'
import { useNavigate, useParams } from 'react-router-dom'
import NewInfoFormat from '../../../../components/template/NewInfoFormat'
import NewInfoInput from '../../../../components/template/NewInfoInput'
import NewInfoSelector from '../../../../components/template/NewInfoSelector'
import NewInfoDescription from '../../../../components/template/NewInfoDescription'
import api from '../../../../components/utils/api';

const ServiceNewModal = ({ mergeRuleId, closeModal }) => {
    const [serviceName, setServiceName] = useState("")
    const [serviceGroup, setServiceGroup] = useState("")
    const [modelType, setModelType] = useState()
    const [convertedModel, setConvertedModel] = useState()
    const [feVariant, setFeVariant] = useState({ label: 'Conv1d (on-device, default)', value: 'conv1d' })
    const [threshold, setThreshold] = useState()
    const [modelTypeOption, setModelTypeOption] = useState([])
    const [convertedModelOption, setConvertedModelOption] = useState({})
    const [thresholdOption, setThresholdOption] = useState([])
    const [isDisabled, setIsDisabled] = useState(true)
    const [generalModel, setGeneralModel] = useState({})

    const navigate = useNavigate()
    const { generalModelId } = useParams()

    const isTorch = useMemo(() => generalModel?.model_type === "Torch", [generalModel?.model_type])

    useEffect(() => {
        if (generalModelId) {
            api.get(`/api/general-models/${generalModelId}`)
                .then(res => setGeneralModel(res.data))
                .catch(err => console.error(err))
        }
    }, [generalModelId])

    useEffect(() => {
        if (mergeRuleId) {
            // get threshold list to set it as an option for threshold
            api.get(`/api/merge-rules/${mergeRuleId}/thresholds`)
                .then(res => setThresholdOption(res.data.thresholds))
                .catch(err => console.error(err))
        }
    }, [mergeRuleId])

    useEffect(() => {
        if (mergeRuleId && generalModel?.model_type) {
            api.get(`/api/merge-rules/${mergeRuleId}/converted-models/framework-map?is_torch=${isTorch}`)
                .then((res) => setConvertedModelOption(res.data.converted_model_map))
                .catch(err => console.error(err))
        }
    }, [mergeRuleId, generalModel?.model_type])

    // Feature-extractor variant selector is offered only when the backend
    // reports more than one variant (onnx/torchscript fp32 mel-based models).
    const feVariantApplicable = useMemo(
        () => (convertedModel?.fe_variants?.length ?? 0) > 1,
        [convertedModel]
    )

    useEffect(() => {
        if (convertedModel) {
            setModelType(undefined)
            setFeVariant({ label: 'Conv1d (on-device, default)', value: 'conv1d' })
            const options = []
            if (isTorch) {
                if (convertedModel.all_status === 'completed')
                    options.push({ label: 'model_all', value: ['model_all'] })
                if (convertedModel.main_status === 'completed')
                    options.push({ label: 'model_main', value: ['model_main'] })
                if (convertedModel.pre_status === 'completed' && convertedModel.main_status === 'completed')
                    options.push({ label: 'model_pre & model_main', value: ['model_pre', 'model_main'] })
            } else {
                if (!convertedModel.label.includes('int8'))
                    options.push({ label: 'model_all', value: ['model_all'] })
                options.push({ label: 'model_main', value: ['model_main'] })
                if (!convertedModel.label.includes('int8'))
                    options.push({ label: 'model_pre & model_main', value: ['model_pre', 'model_main'] })
            }
            setModelTypeOption(options)
        }
    }, [convertedModel, isTorch])

    useEffect(() => {
        setIsDisabled(!(serviceName && serviceGroup && modelType && convertedModel && threshold))
    }, [serviceName, serviceGroup, modelType, convertedModel, threshold])

    const handleExport = async () => {
        try {
            setIsDisabled(true)
            await api.post(`/api/eats-services`, {
                group_name: serviceGroup,
                name: serviceName,
                model_type: modelType.value,
                converted_model_id: convertedModel.value,
                threshold_id: threshold.value,
                fe_variant: feVariant.value,
            })
            navigate(0)
            closeModal()
        } catch (err) {
            console.error(err)
        } finally {
            setIsDisabled(false)
        }
    }

    return (
        <div className='h-full'>
            <MainTitle title="Export a New Service" />
            <div className='md:grid md:grid-cols-2 md:gap-3'>
                {[
                    {
                        label: "Service Group", contents: <NewInfoInput value={serviceGroup} setValue={setServiceGroup} />,
                        description: <NewInfoDescription contents={[
                            "Set a Service Group. Usually, it represents the client's company name. ('Hanhwa_techwin', 'Ghost_robotics', etc.)",
                            "It will determine the path in 'eats-service' bucket. (gs://eats-service/SERVICE_GROUP/service_name)"
                        ]} />
                    },
                    {
                        label: "Service Name", contents: <NewInfoInput value={serviceName} setValue={setServiceName} />,
                        description: <NewInfoDescription contents={[
                            "Set a Service Name. Usually, it literally represents the service name. ('emergency-detection', 'gunshot-detection', etc.)",
                            "It will determine the path in 'eats-service' bucket. (gs://eats-service/service_group/SERVICE_NAME)"
                        ]} />
                    },
                    {
                        label: "Threshold", contents: <NewInfoSelector
                            value={threshold}
                            setValue={setThreshold}
                            options={thresholdOption.map(e => ({ label: e.name, value: e.id }))}
                            category="threshold"
                        />, description: <div></div>
                    },
                    {
                        label: "Converted Model", contents: <NewInfoSelector
                            value={convertedModel}
                            setValue={setConvertedModel}
                            options={Object.entries(convertedModelOption).filter(([_, v]) => Object.keys(v).length !== 0).filter(([_, v]) => v.status === 5).map(([k, v]) => ({ label: k, value: v.id, all_status: v.all_status, main_status: v.main_status, pre_status: v.pre_status, fe_variants: v.fe_variants }))}
                            category="converted model"
                        />, description: <div></div>
                    },
                    {
                        label: "Model Type", contents: <NewInfoSelector
                            value={modelType}
                            setValue={setModelType}
                            options={modelTypeOption}
                            category="model type"
                            noOptionsMessage="Please select a converted model first"
                        />, description: <NewInfoDescription contents={[
                            "Model_pre: a model for preproccssing",
                            "Model_main: a model for inferencing",
                            "Model_all: a model combined with the model_pre and model_main"
                        ]} />
                    },
                    ...(feVariantApplicable ? [{
                        label: "Feature Extractor", contents: <NewInfoSelector
                            value={feVariant}
                            setValue={setFeVariant}
                            options={[
                                { label: 'Conv1d (on-device, default)', value: 'conv1d' },
                                { label: 'STFT (server)', value: 'stft' },
                            ]}
                            category="feature extractor"
                        />, description: <NewInfoDescription contents={[
                            "Conv1d is the portable default for on-device/embedded deployment; STFT is optimized for server-side inference.",
                        ]} />
                    }] : []),
                ].map((e, i) => <NewInfoFormat label={e.label} contents={e.contents} description={e.description} key={i} />)}
            </div>
            <div className='my-4 text-right'>
                <ButtonWrap contents="Export" onClick={() => handleExport()} isDisabled={isDisabled} />
            </div>
        </div >
    )
}

export default ServiceNewModal